import { request } from '@umijs/max';
import { onSocketReconnected, registerWsHandler, WsEventType } from '../websocket';

// ---- Agent Status & Break Codes API ----
// Backend: Controllers/api/AgentStatus/* (CSR self-service + supervisor).
// Only 'available' routes calls. 'on_call' and 'acw' are derived from the
// phone system and cannot be selected; the user picks available | not_ready
// (+ break code) | offline and the backend reports the effective state.

export type AgentState = 'offline' | 'available' | 'on_call' | 'acw' | 'not_ready';
export type AgentSelectableState = 'available' | 'not_ready' | 'offline';
export type AgentStatusSource = 'agent' | 'supervisor' | 'system';

export const AGENT_STATES: AgentState[] = ['available', 'not_ready', 'on_call', 'acw', 'offline'];

export const STATE_LABELS: Record<AgentState, string> = {
  offline: 'Offline',
  available: 'Available',
  on_call: 'On Call',
  acw: 'Wrap-up',
  not_ready: 'Not Ready',
};

/** antd Tag / Badge color per state. */
export const STATE_COLORS: Record<AgentState, string> = {
  available: 'green',
  not_ready: 'orange',
  on_call: 'blue',
  acw: 'purple',
  offline: 'default',
};

/** Plain CSS colors matching STATE_COLORS, for places a Tag preset name is not usable. */
export const STATE_HEX: Record<AgentState, string> = {
  available: '#52c41a',
  not_ready: '#fa8c16',
  on_call: '#1677ff',
  acw: '#722ed1',
  offline: '#8c8c8c',
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Live "time in state" label: m:ss under an hour, h:mm:ss above. */
export function formatStateElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${m}:${pad2(sec)}`;
}

/** Fixed h:mm:ss duration, for history tables. */
export function formatDurationHms(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return '';
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${pad2(m)}:${pad2(sec)}`;
}

export interface BreakCode {
  id: number;
  code: string;
  label: string;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  requires_note: boolean;
  /** Minutes on this break before the "Still on break?" prompt; null = tenant default. */
  prompt_minutes: number | null;
}

export interface BreakPrompt {
  due: boolean;
  minutes_on_break: number;
  /** ISO string of the next scheduled prompt, or null when none is scheduled. */
  next_prompt_at: string | null;
}

// Datetimes ('since', 'selected_at') are server-local 'YYYY-MM-DD HH:mm:ss'
// strings — use dtFormat/parseDt from '@/util' to display them. Prefer
// `seconds_in_state` (server-computed) over diffing `since` for timers so
// client clock skew does not matter.
export interface AgentStatus {
  user_id: number;
  state: AgentState;
  state_label: string;
  since: string;
  seconds_in_state: number;
  break_code: string | null;
  break_label: string | null;
  /** What the user (or a supervisor) picked — may differ from `state` while on a call / in wrap-up. */
  selected_state: AgentSelectableState | null;
  selected_break_code: string | null;
  selected_break_label: string | null;
  note: string | null;
  selected_at: string | null;
  selected_source: AgentStatusSource | null;
  selected_by_user_id: number | null;
  selected_reason: string | null;
  routable: boolean;
  is_logged_in: boolean;
  is_available_flag: boolean;
  break_prompt: BreakPrompt;
}

export interface AgentStatusConfig {
  enabled: boolean;
  break_prompt_minutes: number;
  break_reprompt_minutes: number;
  /** Active break codes only. */
  break_codes: BreakCode[];
  states: Record<string, string>;
}

export interface AgentStatusMePayload {
  status: AgentStatus;
  config: AgentStatusConfig;
}

export interface AgentStatusRow extends AgentStatus {
  user_name: string | null;
  display_name: string | null;
  user_email: string | null;
  profile_image: string | null;
  is_active: boolean;
}

export interface AgentStatusListPayload {
  rows: AgentStatusRow[];
  enabled: boolean;
  break_codes: BreakCode[];
  states: Record<string, string>;
}

export interface AgentStatusHistoryRow {
  id: number;
  user_id: number;
  state: AgentState;
  state_label: string;
  break_code: string | null;
  break_label: string | null;
  note: string | null;
  source: AgentStatusSource | null;
  changed_by_user_id: number | null;
  reason: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  user: { id: number; user_name: string | null } | null;
  changed_by: { id: number; user_name: string | null } | null;
}

export interface AgentStatusHistoryPayload {
  pagination: API.Pagination;
  rows: AgentStatusHistoryRow[];
  states: Record<string, string>;
}

export type SetAgentStatusPayload = {
  state: AgentSelectableState;
  break_code?: string | null;
  note?: string | null;
};

export type SetAgentStatusForUserPayload = SetAgentStatusPayload & {
  /** Required when changing another user's status. */
  reason: string;
};

/** Body of the 422 the set endpoints return on validation failure. */
export type AgentStatusValidationError = {
  success: false;
  error: 'validation_failed';
  message: string;
  fields: Record<string, string>;
};

/** Pulls the user-facing message out of a failed set-status request. */
export function agentStatusErrorMessage(e: any, fallback = 'Failed to update status'): string {
  const data = e?.response?.data;
  if (data?.message && typeof data.message === 'string') return data.message;
  return e?.message || fallback;
}

// ─── CSR self-service ────────────────────────────────────────────────────────

export async function getMyAgentStatus(): Promise<AgentStatusMePayload> {
  return request<API.ResultObject<AgentStatusMePayload>>('/api/tenant/agent-status/me', {
    method: 'GET',
  }).then((res) => res.data);
}

/**
 * Errors are NOT routed through the global umi error handler: callers render
 * the 422 `message` themselves (see agentStatusErrorMessage) so the widget
 * can keep its modal open.
 */
export async function setMyAgentStatus(data: SetAgentStatusPayload): Promise<AgentStatusMePayload> {
  return request<API.ResultObject<AgentStatusMePayload>>('/api/tenant/agent-status/me', {
    method: 'POST',
    data,
    skipErrorHandler: true,
  }).then((res) => res.data);
}

/** "Yes, still on break" — postpones the next break prompt. */
export async function acknowledgeBreakPrompt(): Promise<AgentStatusMePayload> {
  return request<API.ResultObject<AgentStatusMePayload>>('/api/tenant/agent-status/me/break-prompt', {
    method: 'POST',
    skipErrorHandler: true,
  }).then((res) => res.data);
}

// ─── Supervisor (permission agent_status.manage) ─────────────────────────────

export async function listAgentStatuses(): Promise<AgentStatusListPayload> {
  return request<API.ResultObject<AgentStatusListPayload>>('/api/tenant/agent-status', {
    method: 'GET',
  }).then((res) => ({
    rows: res.data?.rows ?? [],
    enabled: !!res.data?.enabled,
    break_codes: res.data?.break_codes ?? [],
    states: res.data?.states ?? {},
  }));
}

/** Errors skip the global handler so the modal can map 422 `fields` onto its form. */
export async function setAgentStatusForUser(
  userId: number,
  data: SetAgentStatusForUserPayload,
): Promise<AgentStatus> {
  return request<API.ResultObject<AgentStatus>>(`/api/tenant/agent-status/users/${userId}`, {
    method: 'POST',
    data,
    skipErrorHandler: true,
  }).then((res) => res.data);
}

export async function getAgentStatusHistory(params: {
  user_id?: number;
  state?: string;
  /** 'YYYY-MM-DD' (or full datetime) bounds, inclusive. */
  from?: string;
  to?: string;
  current?: number;
  pageSize?: number;
}): Promise<{ data: AgentStatusHistoryRow[]; success: boolean; total: number; states: Record<string, string> }> {
  return request<API.ResultObject<AgentStatusHistoryPayload>>('/api/tenant/agent-status/history', {
    method: 'GET',
    params: {
      user_id: params.user_id,
      state: params.state,
      from: params.from,
      to: params.to,
      page: params.current,
      perPage: params.pageSize,
    },
  }).then((res) => ({
    data: res.data?.rows ?? [],
    success: res.success ?? true,
    total: res.data?.pagination?.total_records ?? 0,
    states: res.data?.states ?? {},
  }));
}

// ─── Agent-status event bus (mirrors the callback-queue WS pattern in call-queue.ts) ──
const AGENT_STATUS_UPDATED_EVENT = 'agentStatusUpdated';

/** What an "agent-status-updated" WS push carries — broadcast tenant-wide on every effective change. */
export type AgentStatusUpdatedPayload = {
  user_id: number;
  state: AgentState;
  break_code: string | null;
  since: string;
};

/**
 * `data` is a signal, not a snapshot: the payload only identifies who changed,
 * so listeners re-fetch over REST. Undefined data (socket reconnect / older
 * server) means "something may have changed — refresh everything".
 */
export function emitAgentStatusUpdated(data?: AgentStatusUpdatedPayload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<AgentStatusUpdatedPayload | undefined>(AGENT_STATUS_UPDATED_EVENT, { detail: data }),
  );
}

/** Subscribe to agent-status pushes; returns an unsubscribe function. */
export function onAgentStatusUpdated(handler: (data?: AgentStatusUpdatedPayload) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<AgentStatusUpdatedPayload | undefined>).detail);
  window.addEventListener(AGENT_STATUS_UPDATED_EVENT, listener);
  return () => window.removeEventListener(AGENT_STATUS_UPDATED_EVENT, listener);
}

// Coalescing is safe here because listeners re-fetch on receipt — a burst of
// changes (e.g. a supervisor forcing several agents) collapses into one refresh.
// Note the coalesced payload is the LAST message's; listeners filtering by
// user_id would miss earlier users in the burst, so the CSR widget treats any
// push as a reason to refresh its own status when the user_id is absent.
registerWsHandler(WsEventType.AGENT_STATUS_UPDATED, emitAgentStatusUpdated, { coalesceMs: 300 });

// A dropped push during a disconnect never arrives on its own — resync as soon
// as the socket comes back instead of waiting out the poll interval.
onSocketReconnected(() => emitAgentStatusUpdated());
